import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { sendContactForm } from '../api/api';
import { MILAN_WORKSPACE_ENABLED } from '../config/productFlags';
import './Contact.css';

const WORKSPACE_CONTACT_FAQS = [
  {
    q: 'How quickly can I expect a response?',
    a: 'We typically respond within 24-48 hours on business days (Monday to Friday, IST).',
  },
  {
    q: 'How does Milan AI match creators with brands?',
    a: 'Our AI analyzes engagement rates, audience demographics, content style, and campaign requirements to find the best creator-brand matches.',
  },
  {
    q: 'What are the platform fees?',
    a: 'Milan AI charges only 3.5% success fee on completed campaigns. No upfront costs or hidden charges.',
  },
  {
    q: 'How do payments work?',
    a: 'Payments are secured in escrow. 60% fixed payments released weekly during the monitoring period (1-4 weeks). 40% performance bonus released after monitoring completes.',
  },
  {
    q: 'Is my data secure?',
    a: 'Yes! We use industry-standard encryption and never share your personal data with third parties.',
  },
  {
    q: 'How can I provide feedback?',
    a: 'We value your feedback! Use the contact form above with "Feedback" as the subject to share your thoughts.',
  },
];

const PERF_HUB_CONTACT_FAQS = [
  {
    q: 'How quickly can I expect a response?',
    a: 'We typically respond within 24-48 hours on business days (Monday to Friday, IST).',
  },
  {
    q: 'What is Performance Hub best for?',
    a: 'Brands and agencies that already have creators and post URLs and need verified Instagram metrics, economics, and exports — without Milan handling escrow or discovery.',
  },
  {
    q: 'How does Performance Hub pricing work?',
    a: 'Free tier for small teams; Pro and Enterprise are flat monthly plans. See Pricing for current amounts. There is no campaign success fee on Performance Hub.',
  },
  {
    q: 'Do creators need a Milan account?',
    a: 'No. Creators connect Instagram via a magic-link email. They do not need to sign up for Milan.',
  },
  {
    q: 'Is my data secure?',
    a: 'Yes! We use industry-standard encryption and never share your personal data with third parties.',
  },
  {
    q: 'How can I provide feedback?',
    a: 'We value your feedback! Use the contact form above with "Feedback" as the subject to share your thoughts.',
  },
];

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const faqs = MILAN_WORKSPACE_ENABLED ? WORKSPACE_CONTACT_FAQS : PERF_HUB_CONTACT_FAQS;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const payload = {
        ...formData,
        phone: formData.phone.trim() || null,
      };
      await sendContactForm(payload);
      setSubmitted(true);
      setTimeout(() => {
        setFormData({ name: '', email: '', phone: '', company: '', subject: '', message: '' });
        setSubmitted(false);
      }, 5000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-page">
      <Navbar />
      
      <section className="contact-hero">
        <h1>Get in Touch</h1>
        <p>
          {MILAN_WORKSPACE_ENABLED
            ? "Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible."
            : 'Questions about Performance Hub, plans, or Instagram connect? Send us a message — we typically reply within 24–48 hours.'}
        </p>
      </section>

      <section className="contact-content">
        <div className="contact-info">
          <div className="info-card">
            <div className="info-icon">📍</div>
            <h3>Location</h3>
            <p>India-based</p>
            <p>Remote-first company</p>
          </div>

          <div className="info-card">
            <div className="info-icon">📧</div>
            <h3>Get In Touch</h3>
            <p>Fill out the contact form</p>
            <p>We'll get back to you soon!</p>
          </div>

          <div className="info-card">
            <div className="info-icon">⏰</div>
            <h3>Response Time</h3>
            <p>We typically respond within 24-48 hours</p>
            <p className="hours">Mon - Fri business hours (IST)</p>
          </div>

          <div className="info-card">
            <div className="info-icon">🚀</div>
            <h3>Quick Support</h3>
            <p>
              {MILAN_WORKSPACE_ENABLED
                ? 'Use our contact form for fastest response'
                : 'Mention Performance Hub in your subject for faster routing'}
            </p>
            <p>Select the right category for quicker help</p>
          </div>
        </div>

        <div className="contact-form-container">
          <h2>Send us a Message</h2>
          
          {submitted ? (
            <div className="success-message">
              <span className="success-icon">✓</span>
              <h3>Message Sent!</h3>
              <p>Thank you for reaching out. We'll get back to you within 24-48 hours.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="contact-form">
              {error && <div className="error-message">{error}</div>}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">
                    Phone Number <span className="form-optional">(optional)</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 98765 43210"
                    maxLength={30}
                    autoComplete="tel"
                    inputMode="tel"
                  />
                  <span className="form-hint">We'll only call if your issue is faster to resolve over a call.</span>
                </div>
                <div className="form-group">
                  <label htmlFor="company">
                    Company Name <span className="form-optional">(optional)</span>
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Your company"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="subject">Subject *</label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a topic</option>
                  <option value="general">General Inquiry</option>
                  {!MILAN_WORKSPACE_ENABLED && (
                    <option value="performance_hub">Performance Hub</option>
                  )}
                  <option value="sales">Sales & Partnerships</option>
                  <option value="support">Technical Support</option>
                  <option value="billing">Billing Question</option>
                  <option value="feedback">Feedback</option>
                  <option value="press">Press & Media</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={
                    MILAN_WORKSPACE_ENABLED
                      ? 'Tell us how we can help you...'
                      : 'Tell us about your roster size, plans questions, or Instagram connect issues...'
                  }
                  rows="5"
                  required
                />
              </div>

              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? 'Sending...' : 'Send Message'}
                {!loading && (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
                  </svg>
                )}
              </button>
            </form>
          )}
        </div>
      </section>

      <section className="faq-section">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-grid">
          {faqs.map((item) => (
            <div className="faq-item" key={item.q}>
              <h4>{item.q}</h4>
              <p>{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Contact;
